/**
 * One workflow, open.
 *
 * The canvas has the room. Nodes are added through the picker — from the
 * `+` on any port (wired as it lands), from the Add step button, or from
 * the empty canvas — and edited in the inspector that slides in on the right.
 * Runs live in a drawer along the bottom that stays out of the way until a
 * run lands.
 *
 * The page edits a local draft. Save writes it; Run executes the draft as it
 * is on screen; "Test this step" runs it up to one node so its real output
 * can be mapped into the steps after it. Leaving with unsaved changes asks.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useConnections } from '@/core/connection/connection.context'
import { ErrorNotice, Loading } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Icon } from '@/shared/components/icons'
import { Dock, DockMain, DockPanel, useDock } from '@/shared/components/dock/Dock'
import { useCatalogue, useWorkflow, useWorkflowMutations } from './api'
import { Canvas } from './canvas/Canvas'
import { GAP_X, GAP_Y, NODE_W, newId, nodeHeight, outputsOf, type Point } from './canvas/geometry'
import { NodeIcon } from './canvas/icons'
import { NodeInspector } from './editor/NodeInspector'
import { NodePicker, type Pick } from './editor/NodePicker'
import { RunPanel, pillFor } from './editor/RunPanel'
import { Field } from './editor/inputs'
import type { AppSpec, NodeSpec, Run, StepRecord, WfEdge, WfNode, Workflow, WorkflowIn } from './types'
import './workflows.css'

type Selection = { kind: 'node' | 'edge'; id: string } | null
type PickerState = { from: { nodeId: string; port: string } | null } | null

export function WorkflowEditorPage() {
  const { workflowId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const toast = useToast()
  const catalogue = useCatalogue()
  const stored = useWorkflow(workflowId)
  const { update, run: runMutation, remove } = useWorkflowMutations()

  const specs = useMemo(
    () => Object.fromEntries((catalogue.data?.nodes ?? []).map((n) => [n.type, n])) as Record<string, NodeSpec>,
    [catalogue.data],
  )
  const apps = useMemo(() => catalogue.data?.apps ?? [], [catalogue.data])
  const appsById = useMemo(() => Object.fromEntries(apps.map((a) => [a.id, a])) as Record<string, AppSpec>, [apps])
  const { byId: connectionsById } = useConnections()

  const [draft, setDraft] = useState<Workflow | null>(null)
  const [dirty, setDirty] = useState(false)
  const [selected, setSelected] = useState<Selection>(null)
  const [picker, setPicker] = useState<PickerState>(null)
  const [activeRunId, setActiveRunId] = useState<string | null>(() => search.get('run'))
  const [latestRun, setLatestRun] = useState<Run | null>(null)
  const [testingNode, setTestingNode] = useState<string | null>(null)
  const dock = useDock('workflow.editor.v2', { inspector: 'min', runs: 'min' })

  useEffect(() => {
    if (search.get('run')) dock.reveal('runs')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (stored.data && !dirty) setDraft(stored.data.workflow)
  }, [stored.data, dirty])

  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // `/` opens the picker from wherever you are, like a command palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable) return
      if (e.key === '/' && !picker) {
        e.preventDefault()
        openPicker()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker, selected, draft])

  const patchDraft = useCallback((fn: (d: Workflow) => Workflow) => {
    setDraft((d) => (d ? fn(d) : d))
    setDirty(true)
  }, [])

  // --- adding nodes -------------------------------------------------------------------------

  const hasTrigger = Boolean(draft?.nodes.some((n) => n.type.startsWith('trigger.')))

  function openPicker(from?: { nodeId: string; port: string } | null) {
    if (from !== undefined) return setPicker({ from })
    // No port given: continue from the selected node's first free port, else from the tail.
    const d = draft
    if (!d) return
    const pickFrom = (n: WfNode | undefined) => {
      if (!n) return null
      const outs = outputsOf(n, specs[n.type])
      if (!outs.length) return null
      const free = outs.find((p) => !d.edges.some((e) => e.source === n.id && e.source_port === p))
      return { nodeId: n.id, port: free ?? outs[0] }
    }
    const sel = selected?.kind === 'node' ? d.nodes.find((n) => n.id === selected.id) : undefined
    const tail = d.nodes.filter((n) => !d.edges.some((e) => e.source === n.id)).sort((a, b) => b.position.y - a.position.y)[0]
    setPicker({ from: pickFrom(sel) ?? pickFrom(tail) })
  }

  const placeAfter = useCallback(
    (d: Workflow, from: { nodeId: string; port: string } | null): Point => {
      const taken = (p: Point) =>
        d.nodes.some((n) => Math.abs(n.position.x - p.x) < NODE_W - 20 && Math.abs(n.position.y - p.y) < nodeHeight(n, specs[n.type]) + 20)
      let pos: Point
      if (from) {
        const src = d.nodes.find((n) => n.id === from.nodeId)!
        const outs = outputsOf(src, specs[src.type])
        const idx = Math.max(0, outs.indexOf(from.port))
        // Below the source; a branch port fans its child out sideways so
        // siblings from different ports sit next to each other.
        const spread = outs.length > 1 ? (idx - (outs.length - 1) / 2) * (NODE_W + GAP_X) : 0
        pos = { x: Math.round(src.position.x + spread), y: src.position.y + nodeHeight(src, specs[src.type]) + GAP_Y }
      } else {
        const last = d.nodes.slice().sort((a, b) => b.position.y - a.position.y)[0]
        pos = last ? { x: last.position.x, y: last.position.y + nodeHeight(last, specs[last.type]) + GAP_Y } : { x: 120, y: 80 }
      }
      // Taken? Slide right until there is room.
      let guard = 0
      while (taken(pos) && guard++ < 20) pos = { x: pos.x + NODE_W + GAP_X, y: pos.y }
      return pos
    },
    [specs],
  )

  const addNode = useCallback(
    (pick: Pick, from: { nodeId: string; port: string } | null) => {
      const spec = specs[pick.type]
      if (!spec || !draft) return
      let name = pick.name
      let n = 2
      while (draft.nodes.some((x) => x.name === name)) name = `${pick.name} ${n++}`
      const node: WfNode = { id: newId('n'), type: pick.type, name, params: pick.params, position: placeAfter(draft, from) }
      const edge: WfEdge | null = from && spec.has_input ? { id: newId('e'), source: from.nodeId, source_port: from.port, target: node.id } : null
      patchDraft((d) => ({ ...d, nodes: [...d.nodes, node], edges: edge ? [...d.edges, edge] : d.edges }))
      setPicker(null)
      setSelected({ kind: 'node', id: node.id })
      dock.reveal('inspector')
    },
    [specs, draft, patchDraft, placeAfter, dock],
  )

  // --- graph edits ----------------------------------------------------------------------

  const moveNode = useCallback(
    (id: string, position: Point) => patchDraft((d) => ({ ...d, nodes: d.nodes.map((n) => (n.id === id ? { ...n, position } : n)) })),
    [patchDraft],
  )

  const connect = useCallback(
    (source: string, port: string, target: string) =>
      patchDraft((d) => {
        if (d.edges.some((e) => e.source === source && e.source_port === port && e.target === target)) return d
        return { ...d, edges: [...d.edges, { id: newId('e'), source, source_port: port, target }] }
      }),
    [patchDraft],
  )

  const deleteSel = useCallback(
    (sel: Selection) => {
      if (!sel) return
      patchDraft((d) =>
        sel.kind === 'node'
          ? { ...d, nodes: d.nodes.filter((n) => n.id !== sel.id), edges: d.edges.filter((e) => e.source !== sel.id && e.target !== sel.id) }
          : { ...d, edges: d.edges.filter((e) => e.id !== sel.id) },
      )
      setSelected(null)
    },
    [patchDraft],
  )

  const changeNode = useCallback(
    (id: string, patch: Partial<WfNode>) => patchDraft((d) => ({ ...d, nodes: d.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) })),
    [patchDraft],
  )

  // --- save / run --------------------------------------------------------------------------

  const asInput = (d: Workflow): WorkflowIn => ({
    name: d.name,
    description: d.description ?? null,
    active: d.active,
    nodes: d.nodes,
    edges: d.edges,
    variables: d.variables,
    settings: d.settings,
    tags: d.tags,
  })

  async function save(extra?: Partial<WorkflowIn>) {
    if (!draft) return
    try {
      const res = await update.mutateAsync({ id: workflowId, body: { ...asInput(draft), ...extra } })
      setDraft(res.workflow)
      setDirty(false)
      toast.success('Saved', `Version ${res.workflow.version}.`)
    } catch (e) {
      toast.error(e)
    }
  }

  async function runNow() {
    if (!draft) return
    try {
      const res = await runMutation.mutateAsync({ id: workflowId, body: { draft: asInput(draft) } })
      setLatestRun(res.run)
      setActiveRunId(res.run.run_id)
      dock.reveal('runs')
      if (res.run.status === 'success') toast.success('Run finished', `${res.run.step_count} steps in ${res.run.duration_ms} ms.`)
      else toast.error(new Error(res.run.error ?? 'The run failed.'), 'Run failed')
    } catch (e) {
      toast.error(e)
    }
  }

  async function testStep(nodeId: string) {
    if (!draft) return
    setTestingNode(nodeId)
    try {
      const res = await runMutation.mutateAsync({ id: workflowId, body: { draft: asInput(draft), until_node_id: nodeId } })
      setLatestRun(res.run)
      setActiveRunId(res.run.run_id)
      const step = res.run.steps.find((s) => s.node_id === nodeId)
      if (res.run.status === 'success' && step) {
        const count = Object.values(step.output_counts).reduce((a, b) => a + b, 0)
        toast.success('Step ran', `${count} item${count === 1 ? '' : 's'} out. Map them from the steps after it.`)
      } else if (res.run.status === 'success') {
        toast.success('Ran', 'The step was not reached — nothing flowed into it. Check the wires and branches before it.')
      } else toast.error(new Error(res.run.error ?? 'The step failed.'), 'Step failed')
    } catch (e) {
      toast.error(e)
    } finally {
      setTestingNode(null)
    }
  }

  async function toggleActive() {
    if (!draft) return
    const next = !draft.active
    if (dirty) return toast.error(new Error('Save your changes before activating.'), 'Unsaved changes')
    if (next && !hasTrigger) return toast.error(new Error('Add a trigger first.'), 'Nothing starts this workflow')
    try {
      const res = await update.mutateAsync({ id: workflowId, body: { active: next } })
      setDraft(res.workflow)
      toast.success(next ? 'Activated' : 'Deactivated', next ? 'Webhook, schedule and app triggers are live.' : 'Triggers are paused.')
    } catch (e) {
      toast.error(e)
    }
  }

  async function destroy() {
    if (!draft || !window.confirm(`Delete "${draft.name}" and its runs?`)) return
    try {
      await remove.mutateAsync(workflowId)
      navigate('/workflows')
    } catch (e) {
      toast.error(e)
    }
  }

  // --- derived --------------------------------------------------------------------------------

  const stepsByNode = useMemo(() => {
    const run = latestRun && latestRun.run_id === activeRunId ? latestRun : null
    const map: Record<string, StepRecord> = {}
    for (const s of run?.steps ?? []) map[s.node_id] = s
    return map
  }, [latestRun, activeRunId])

  // Which app each node belongs to, for the card's logo — by the connector it
  // names, or by the connection it uses when it predates that.
  const appOfNode = useMemo(() => {
    const out: Record<string, AppSpec> = {}
    for (const n of draft?.nodes ?? []) {
      const id = String(n.params.connector_id ?? connectionsById[String(n.params.connection_id ?? '')]?.connector_id ?? '')
      if (id && appsById[id]) out[n.id] = appsById[id]
    }
    return out
  }, [draft?.nodes, appsById, connectionsById])

  const selectedNode = selected?.kind === 'node' ? draft?.nodes.find((n) => n.id === selected.id) : undefined
  const selectNode = useCallback(
    (sel: Selection) => {
      setSelected(sel)
      if (sel?.kind === 'node') dock.reveal('inspector')
    },
    [dock],
  )
  const lastRunStatus = latestRun?.status ?? draft?.last_run_status ?? null

  if (stored.isLoading || catalogue.isLoading) return <Loading label="Opening the workflow…" />
  if (stored.error) return <div className="page"><ErrorNotice error={stored.error} onRetry={() => stored.refetch()} /></div>
  if (!draft || !catalogue.data) return null

  const trigger = draft.nodes.find((n) => n.type.startsWith('trigger.'))

  const topbar = (
    <header className="wf-topbar">
      <Link to="/workflows" className="wf-back" title="All workflows" aria-label="All workflows">
        <Icon.chevronLeft />
      </Link>
      <div className="wf-titlewrap">
        <input className="wf-title" value={draft.name} aria-label="Workflow name" onChange={(e) => patchDraft((d) => ({ ...d, name: e.target.value }))} />
        <div className="wf-status">
          {dirty ? <span className="pill pill-warning">Unsaved</span> : <span className="small muted">v{draft.version}</span>}
          <span className="small muted">·</span>
          <span className="small muted">{draft.nodes.length} step{draft.nodes.length === 1 ? '' : 's'}</span>
          {trigger && (
            <>
              <span className="small muted">·</span>
              <span className="small muted wf-status-trigger"><NodeIcon name={specs[trigger.type]?.icon ?? 'play'} /> {trigger.name}</span>
            </>
          )}
        </div>
      </div>
      <span className="spacer" />
      <div className="wf-actions">
        <button type="button" className="btn btn-sm" onClick={runNow} disabled={runMutation.isPending || draft.nodes.length === 0} title="Run the whole workflow with the trigger's sample input">
          <Icon.play className="wf-btnicon" />
          {runMutation.isPending && !testingNode ? 'Running…' : 'Run'}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => save()} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="wf-switch"
          role="switch"
          aria-checked={draft.active}
          data-on={draft.active || undefined}
          onClick={toggleActive}
          disabled={update.isPending}
          title={draft.active ? 'Live: triggers fire on their own' : 'Off: runs only when you press Run'}
        >
          <span className="wf-switch-knob" />
          <span className="wf-switch-label">{draft.active ? 'Live' : 'Off'}</span>
        </button>
        <button type="button" className="btn btn-ghost btn-sm wf-danger" onClick={destroy} aria-label="Delete workflow" title="Delete workflow">
          <Icon.trash className="wf-btnicon" />
        </button>
      </div>
    </header>
  )

  return (
    <>
      <Dock className="wf-editor" top={topbar} right={dock.state.inspector} bottom={dock.state.runs} sizes={{ right: 400, bottom: 300 }}>
        <DockMain>
          <Canvas
            nodes={draft.nodes}
            edges={draft.edges}
            specs={specs}
            apps={appOfNode}
            selected={selected}
            onSelect={selectNode}
            onMove={moveNode}
            onConnect={connect}
            onDelete={deleteSel}
            onAddFrom={(nodeId, port) => openPicker({ nodeId, port })}
            onAddFirst={() => openPicker(null)}
            steps={stepsByNode}
            fitKey={`${dock.state.inspector}/${dock.state.runs}`}
          />
          {draft.nodes.length > 0 && (
            <button type="button" className="wf-addstep" onClick={() => openPicker()} title="Add a step  ( / )">
              <span className="wf-addstep-plus">+</span> Add step
              <kbd className="wf-kbd">/</kbd>
            </button>
          )}
        </DockMain>

        <DockPanel
          side="right"
          title={selectedNode ? 'Step' : 'Workflow'}
          icon={<Icon.sliders />}
          state={dock.state.inspector}
          onState={(st) => dock.set('inspector', st)}
          summary={selectedNode ? <span className="pill">{selectedNode.name}</span> : undefined}
          actions={
            selectedNode ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>Workflow settings</button>
            ) : undefined
          }
        >
          {selectedNode ? (
            <NodeInspector
              key={selectedNode.id}
              node={selectedNode}
              spec={specs[selectedNode.type]}
              draft={draft}
              specs={specs}
              apps={apps}
              step={stepsByNode[selectedNode.id]}
              steps={stepsByNode}
              testing={testingNode === selectedNode.id}
              onChange={(patch) => changeNode(selectedNode.id, patch)}
              onDelete={() => deleteSel({ kind: 'node', id: selectedNode.id })}
              onTest={() => testStep(selectedNode.id)}
            />
          ) : (
            <WorkflowSettings draft={draft} patchDraft={patchDraft} />
          )}
        </DockPanel>

        <DockPanel
          side="bottom"
          title="Runs"
          icon={<Icon.history />}
          state={dock.state.runs}
          onState={(st) => dock.set('runs', st)}
          summary={lastRunStatus ? <span className={`pill ${pillFor(lastRunStatus)}`}>last run {lastRunStatus}</span> : <span className="muted">No runs yet</span>}
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={runNow} disabled={runMutation.isPending || draft.nodes.length === 0}>
              {runMutation.isPending ? 'Running…' : 'Run now'}
            </button>
          }
        >
          <RunPanel
            workflowId={workflowId}
            activeRunId={activeRunId}
            latest={latestRun}
            onPickRun={(id) => {
              setActiveRunId(id)
              if (id && latestRun?.run_id !== id) setLatestRun(null)
            }}
            onPickNode={(id) => selectNode({ kind: 'node', id })}
          />
        </DockPanel>
      </Dock>

      {picker && (
        <NodePicker
          catalogue={catalogue.data}
          needsTrigger={!hasTrigger}
          afterPort={Boolean(picker.from)}
          onPick={(pick) => addNode(pick, picker.from)}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  )
}

// --- the workflow's own settings, shown when nothing is selected ----------------------------------

function WorkflowSettings({ draft, patchDraft }: { draft: Workflow; patchDraft: (fn: (d: Workflow) => Workflow) => void }) {
  const rows = Object.entries(draft.variables)
  const setVars = (v: Record<string, unknown>) => patchDraft((d) => ({ ...d, variables: v }))
  return (
    <div className="wf-inspector wf-inspector-empty">
      <div className="wf-empty-hint">
        <Icon.sliders />
        <div>
          <strong>Workflow settings</strong>
          <p className="muted small">Select a step to configure it. Press <strong>+</strong> on a step to add the next one.</p>
        </div>
      </div>
      <section className="wf-section">
        <Field label="Description">
          <textarea className="textarea" rows={2} value={draft.description ?? ''} placeholder="What this workflow does" onChange={(e) => patchDraft((d) => ({ ...d, description: e.target.value }))} />
        </Field>
      </section>
      <section className="wf-section">
        <Field label="Variables" help={<>Read anywhere as <code>$vars.name</code>, or pick them from the data picker.</>}>
          <div className="wf-rows">
            {rows.map(([k, v], i) => (
              <div className="wf-row" key={i}>
                <input className="input input-mono" value={k} placeholder="name" aria-label="Variable name" onChange={(e) => setVars(Object.fromEntries(rows.map(([kk, vv], idx) => (idx === i ? [e.target.value, vv] : [kk, vv]))))} />
                <input className="input" value={typeof v === 'string' ? v : JSON.stringify(v)} placeholder="value" aria-label="Variable value" onChange={(e) => setVars({ ...draft.variables, [k]: e.target.value })} />
                <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label="Remove" onClick={() => setVars(Object.fromEntries(rows.filter((_, idx) => idx !== i)))}>×</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm" onClick={() => setVars({ ...draft.variables, [`var_${rows.length + 1}`]: '' })}>+ Add variable</button>
          </div>
        </Field>
      </section>
      <section className="wf-section">
        <h4 className="wf-section-title">Limits</h4>
        <div className="wf-limits">
          <label>
            <span className="small muted">Max steps</span>
            <input className="input" type="number" value={draft.settings.max_steps} onChange={(e) => patchDraft((d) => ({ ...d, settings: { ...d.settings, max_steps: Number(e.target.value) } }))} />
          </label>
          <label>
            <span className="small muted">Timeout (s)</span>
            <input className="input" type="number" value={draft.settings.timeout_s} onChange={(e) => patchDraft((d) => ({ ...d, settings: { ...d.settings, timeout_s: Number(e.target.value) } }))} />
          </label>
          <label>
            <span className="small muted">Loop cap</span>
            <input className="input" type="number" value={draft.settings.max_loop_iterations} onChange={(e) => patchDraft((d) => ({ ...d, settings: { ...d.settings, max_loop_iterations: Number(e.target.value) } }))} />
          </label>
        </div>
      </section>
    </div>
  )
}
